import { useEffect, useMemo } from 'preact/hooks';

import { CustomMessage, Message } from 'Components/message/index';
import type { MessageTemplateParams } from 'Components/message/message';
import { Pagination, PaginationMenu } from 'Components/pagination/index';
import type { PaginationTemplateParams } from 'Components/pagination/pagination';
import type { PaginationMenuTemplateParams } from 'Components/pagination/paginationMenu';
import type {
  ContextDialogShellProps,
  TemplateContextMap,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import { useIdleEffect } from 'Core/hooks/index';
import { isVehicleSelectedSelector } from 'Core/selectors/fitmentSearch/index';
import {
  pageFromSelector,
  pageToSelector,
  searchQuerySelector,
  searchResponseExtraSelector,
  searchResponseMessagesSelector,
  searchResponseStateSelector,
  totalHitsSelector,
} from 'Core/selectors/search';
import { resultsLoadingSelector, viewSelector } from 'Core/selectors/show';
import { useSelector } from 'Core/store';
import uiConfig from 'Models/uiConfig/uiConfig';
import { safeCallback } from 'Utils/safeCallback';
import isMobile from 'Vendor/isMobile';

interface Props extends ContextDialogShellProps<SearchPageTemplateParams> {
  updateCallback?: () => void;
}

export interface SearchPageTemplateParams extends TemplateContextMap {
  customMessage: TemplateFunctionInvoker<void>;
  isLoading: boolean;
  isMobile: boolean;
  isVehicleSelected: boolean;
  message: TemplateFunctionInvoker<MessageTemplateParams>;
  pageFrom: number;
  pageTo: number;
  pagination: TemplateFunctionInvoker<PaginationTemplateParams>;
  paginationMenu: TemplateFunctionInvoker<PaginationMenuTemplateParams>;
  responseExtra: ReturnType<typeof searchResponseExtraSelector>;
  responseMessages: ReturnType<typeof searchResponseMessagesSelector>;
  responseQuery: ReturnType<typeof searchQuerySelector>;
  responseState: ReturnType<typeof searchResponseStateSelector>;
  searchPageUrl: string | undefined;
  totalHits: number;
  view: string;
}

export default function SearchPage({ context, template, updateCallback = () => {} }: Props): TemplateResult {
  const responseState = useSelector(searchResponseStateSelector);
  const responseQuery = useSelector(searchQuerySelector);
  const responseExtra = useSelector(searchResponseExtraSelector);
  const pageFrom = useSelector(pageFromSelector);
  const pageTo = useSelector(pageToSelector);
  const totalHits = useSelector(totalHitsSelector);
  const view = useSelector(viewSelector);
  const isLoading = useSelector(resultsLoadingSelector);
  const isVehicleSelected = useSelector(isVehicleSelectedSelector);
  const responseMessages = useSelector(searchResponseMessagesSelector);

  useEffect(() => {
    window.document.body.classList.add('cm_hasSearchPage');
  }, []);

  const idleUpdateCallback = useMemo(
    () => safeCallback(updateCallback, 'update_callback', { component: 'SearchPage' }),
    [updateCallback],
  );

  useIdleEffect(idleUpdateCallback);

  const message = (templ: TemplateFunction<MessageTemplateParams>) => (
    <Message template={templ} key="message" />
  );
  const customMessage = (templ: TemplateFunction<void>) => (
    <CustomMessage template={templ} key="custom-message" />
  );
  const pagination = (templ: TemplateFunction<PaginationTemplateParams>) => (
    <Pagination template={templ} key="pagination" />
  );
  const paginationMenu = (templ: TemplateFunction<PaginationMenuTemplateParams>) => (
    <PaginationMenu template={templ} key="pagination-menu" />
  );

  return template.call({
    ...context,
    pagination,
    paginationMenu,
    message,
    customMessage,
    isLoading,
    isMobile,
    responseState,
    responseQuery,
    responseExtra,
    searchPageUrl: uiConfig.searchPageUrl,
    pageFrom,
    pageTo,
    totalHits,
    view,
    isVehicleSelected,
    responseMessages,
  });
}
